import React from 'react';
import DOMPurify from 'dompurify';
import { Conversation } from '../useData';
import { formatDateTime } from '../../../config/appConfig';
import { Button } from '../../../components/ui';
import MessageAttachment from './MessageAttachment';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface ConversationThreadProps {
  conversations: Conversation[];
  onReply?: (conversation: Conversation) => void;
  onMarkAsRead?: (id: number) => void;
  onDelete?: (id: number) => void;
  currentUserId?: number;
}

const ConversationThread: React.FC<ConversationThreadProps> = ({
  conversations,
  onReply,
  onMarkAsRead,
  onDelete,
  currentUserId,
}) => {
  const { colors } = useTheme();

  // Build thread hierarchy
  const buildThread = (conversations: Conversation[]): Conversation[] => {
    const rootMessages = conversations.filter((c) => !c.replyToId);
    const repliesMap = new Map<number, Conversation[]>();

    conversations.forEach((c) => {
      if (c.replyToId) {
        if (!repliesMap.has(c.replyToId)) {
          repliesMap.set(c.replyToId, []);
        }
        repliesMap.get(c.replyToId)!.push(c);
      }
    });

    const addReplies = (message: Conversation): Conversation => {
      const replies = repliesMap.get(message.id) || [];
      return {
        ...message,
        replies: replies.map(addReplies),
      };
    };

    return rootMessages.map(addReplies);
  };

  const threadMessages = buildThread(conversations);

  const renderMessage = (message: Conversation, depth: number = 0): React.ReactNode => {
    const senderName =
      message.senderType === 'STAFF' && message.staffUser
        ? message.staffUser.userDisplayName ||
          `${message.staffUser.userFirstName || ''} ${message.staffUser.userLastName || ''}`.trim() ||
          `User #${message.staffUser.id}`
        : message.senderType === 'CLIENT' && message.client
        ? message.client.clientName || `Client #${message.client.clientId}`
        : message.senderType;

    const isUnread = !message.isRead;
    const marginLeft = depth * 24;

    return (
      <div key={message.id} className="mb-4">
        <div
          className={cn(
            'border rounded-lg p-4',
            isUnread ? colors.surfaceHover : colors.surface,
            colors.border
          )}
          style={{ marginLeft: `${marginLeft}px` }}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn('font-semibold', colors.text)}>{senderName}</span>
                <span
                  className={cn(
                    'px-2 py-1 rounded text-xs font-medium',
                    message.senderType === 'STAFF' ? colors.info : colors.success
                  )}
                >
                  {message.senderType}
                </span>
                {isUnread && (
                  <span className={cn('w-2 h-2 rounded-full shrink-0', colors.primary)} title="Unread" />
                )}
              </div>
              <p className={cn('text-sm', colors.textMuted)}>{formatDateTime(message.createdAt)}</p>
            </div>
            <div className="flex items-center gap-2">
              {onReply && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onReply(message)}
                  className={cn(colors.textSecondary, colors.interactiveHover)}
                >
                  Reply
                </Button>
              )}
              {onMarkAsRead && isUnread && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMarkAsRead(message.id)}
                  className={cn(colors.textMuted, colors.interactiveHover)}
                >
                  Mark as Read
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(message.id)}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
          <div
            className="prose prose-sm max-w-none mt-3 dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.message || '') }}
          />
          {message.attachments && message.attachments.length > 0 && (
            <div className={cn('mt-3 pt-3 border-t', colors.border)}>
              <p className={cn('text-sm font-medium mb-2', colors.textSecondary)}>Attachments:</p>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((attachment) => (
                  <MessageAttachment key={attachment.id} attachment={attachment} />
                ))}
              </div>
            </div>
          )}
        </div>
        {message.replies && message.replies.length > 0 && (
          <div className="mt-2">
            {message.replies.map((reply) => renderMessage(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (threadMessages.length === 0) {
    return (
      <div className={cn('text-center py-8', colors.textMuted)}>
        <p>No conversations yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {threadMessages.map((message) => renderMessage(message))}
    </div>
  );
};

export default ConversationThread;
